import { ArrowLeft, Edit, MoveDown, MoveUp, Plus, Save, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useTemplateBuilderData } from "@/hooks/template-builder/useTemplateBuilderData";
import { useTemplateBuilderMutations } from "@/hooks/template-builder/useTemplateBuilderMutations";
import { useTemplateBuilderState } from "@/hooks/template-builder/useTemplateBuilderState";
import type { ComponentType, RenovationService, TemplateType } from "@/types/components";

interface TemplateBuilderProps {
  templateId?: number | null;
  onClose: () => void;
}

const TemplateBuilder = ({ templateId, onClose }: TemplateBuilderProps) => {
  // Data fetching
  const { templateData, services, isLoading, getServiceName } = useTemplateBuilderData({
    templateId,
  });

  // State management
  const {
    template,
    templateChecklists,
    editingChecklist,
    showChecklistDialog,
    showComponentDialog,
    editingComponent,
    setTemplate,
    setEditingChecklist,
    setShowChecklistDialog,
    setShowComponentDialog,
    setEditingComponent,
    addChecklist,
    editChecklist,
    deleteChecklist,
    moveChecklist,
    saveChecklist,
    addComponent,
    editComponent,
    saveComponent,
    deleteComponent,
  } = useTemplateBuilderState({ templateData, templateId, services });

  // Mutations
  const { handleSaveTemplate, isSaving } = useTemplateBuilderMutations({
    templateId,
    templateChecklists,
    onClose,
  });

  if (isLoading || !template) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading template...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Templates
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {templateId ? "Edit Template" : "Create Template"}
            </h1>
            <p className="text-gray-600 mt-1">
              Build your template by adding checklists and components
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => handleSaveTemplate(template)} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Template Settings */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Template Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  value={template.name}
                  onChange={(e) =>
                    setTemplate((prev) => (prev ? { ...prev, name: e.target.value } : null))
                  }
                  placeholder="Template name..."
                />
              </div>

              <div>
                <Label htmlFor="template-description">Description</Label>
                <Textarea
                  id="template-description"
                  value={template.description || ""}
                  onChange={(e) =>
                    setTemplate((prev) => (prev ? { ...prev, description: e.target.value } : null))
                  }
                  placeholder="Template description..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="template-type">Template Type</Label>
                <Select
                  value={template.template_type}
                  onValueChange={(value: TemplateType) =>
                    setTemplate((prev) => (prev ? { ...prev, template_type: value } : null))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bathroom">Bathroom</SelectItem>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="laundry">Laundry</SelectItem>
                    <SelectItem value="whole_home">Whole Home</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is-active"
                  checked={template.is_active}
                  onCheckedChange={(checked) =>
                    setTemplate((prev) => (prev ? { ...prev, is_active: checked } : null))
                  }
                />
                <Label htmlFor="is-active">Active Template</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is-default"
                  checked={template.is_default}
                  onCheckedChange={(checked) =>
                    setTemplate((prev) => (prev ? { ...prev, is_default: checked } : null))
                  }
                />
                <Label htmlFor="is-default">Default Template</Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Template Checklists */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Template Checklists
                <Button onClick={addChecklist}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Checklist
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {templateChecklists.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No checklists added yet</p>
                  <Button onClick={addChecklist}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Checklist
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {templateChecklists.map((checklist, index) => (
                    <Card key={index} className="border-l-4 border-l-blue-500">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{index + 1}</Badge>
                            <div>
                              <div className="font-medium">{checklist.name}</div>
                              <div className="text-sm text-gray-500">
                                {getServiceName(checklist.service_id)}
                              </div>
                            </div>
                            {checklist.is_required && <Badge variant="secondary">Required</Badge>}
                          </div>

                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => moveChecklist(index, "up")}
                              disabled={index === 0}
                            >
                              <MoveUp className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => moveChecklist(index, "down")}
                              disabled={index === templateChecklists.length - 1}
                            >
                              <MoveDown className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => editChecklist(checklist)}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteChecklist(index)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>

                      <CardContent>
                        {checklist.description && (
                          <p className="text-sm text-gray-600 mb-3">{checklist.description}</p>
                        )}

                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium">
                            Components ({checklist.components.length})
                          </p>
                          <Button size="sm" variant="outline" onClick={() => addComponent(index)}>
                            <Plus className="w-3 h-3 mr-1" />
                            Add Component
                          </Button>
                        </div>

                        {checklist.components.length > 0 ? (
                          <div className="space-y-2">
                            {checklist.components.map((component, compIndex) => (
                              <div
                                key={compIndex}
                                className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                              >
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className="text-xs">
                                    {component.component_type}
                                  </Badge>
                                  <span className="font-medium text-sm">{component.label}</span>
                                  {component.is_required && (
                                    <Badge variant="secondary" className="text-xs">
                                      Required
                                    </Badge>
                                  )}
                                  {component.base_price && component.base_price > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      ${component.base_price}
                                    </Badge>
                                  )}
                                </div>

                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => editComponent(index, compIndex)}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteComponent(index, compIndex)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No components yet</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Checklist Edit Dialog */}
      <Dialog open={showChecklistDialog} onOpenChange={setShowChecklistDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingChecklist?.id ? "Edit Checklist" : "Add Checklist"}</DialogTitle>
            <DialogDescription>
              Configure the checklist details and service association
            </DialogDescription>
          </DialogHeader>

          {editingChecklist && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="checklist-name">Checklist Name</Label>
                <Input
                  id="checklist-name"
                  value={editingChecklist.name}
                  onChange={(e) =>
                    setEditingChecklist((prev) => (prev ? { ...prev, name: e.target.value } : null))
                  }
                  placeholder="Checklist name..."
                />
              </div>

              <div>
                <Label htmlFor="checklist-description">Description</Label>
                <Textarea
                  id="checklist-description"
                  value={editingChecklist.description}
                  onChange={(e) =>
                    setEditingChecklist((prev) =>
                      prev ? { ...prev, description: e.target.value } : null
                    )
                  }
                  placeholder="Checklist description..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="checklist-service">Service</Label>
                <Select
                  value={editingChecklist.service_id.toString()}
                  onValueChange={(value) =>
                    setEditingChecklist((prev) =>
                      prev ? { ...prev, service_id: Number.parseInt(value, 10) } : null
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service: RenovationService) => (
                      <SelectItem key={service.id} value={service.id.toString()}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="checklist-required"
                  checked={editingChecklist.is_required}
                  onCheckedChange={(checked) =>
                    setEditingChecklist((prev) => (prev ? { ...prev, is_required: checked } : null))
                  }
                />
                <Label htmlFor="checklist-required">Required Checklist</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowChecklistDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={() => saveChecklist(editingChecklist)}>Save Checklist</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Component Edit Dialog */}
      <Dialog open={showComponentDialog} onOpenChange={setShowComponentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingComponent?.id ? "Edit Component" : "Add Component"}</DialogTitle>
            <DialogDescription>Configure the component details and behavior</DialogDescription>
          </DialogHeader>

          {editingComponent && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="component-label">Label</Label>
                  <Input
                    id="component-label"
                    value={editingComponent.label}
                    onChange={(e) =>
                      setEditingComponent((prev) =>
                        prev ? { ...prev, label: e.target.value } : null
                      )
                    }
                    placeholder="Component label..."
                  />
                </div>

                <div>
                  <Label htmlFor="component-type">Type</Label>
                  <Select
                    value={editingComponent.component_type}
                    onValueChange={(value: ComponentType) =>
                      setEditingComponent((prev) =>
                        prev ? { ...prev, component_type: value } : null
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="textarea">Textarea</SelectItem>
                      <SelectItem value="select">Select</SelectItem>
                      <SelectItem value="radio">Radio</SelectItem>
                      <SelectItem value="checkbox">Checkbox</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="field-name">Field Name</Label>
                <Input
                  id="field-name"
                  value={editingComponent.field_name}
                  onChange={(e) =>
                    setEditingComponent((prev) =>
                      prev ? { ...prev, field_name: e.target.value } : null
                    )
                  }
                  placeholder="field_name"
                />
              </div>

              <div>
                <Label htmlFor="placeholder">Placeholder</Label>
                <Input
                  id="placeholder"
                  value={editingComponent.placeholder || ""}
                  onChange={(e) =>
                    setEditingComponent((prev) =>
                      prev ? { ...prev, placeholder: e.target.value } : null
                    )
                  }
                  placeholder="Placeholder text..."
                />
              </div>

              <div>
                <Label htmlFor="help-text">Help Text</Label>
                <Input
                  id="help-text"
                  value={editingComponent.help_text || ""}
                  onChange={(e) =>
                    setEditingComponent((prev) =>
                      prev ? { ...prev, help_text: e.target.value } : null
                    )
                  }
                  placeholder="Help text..."
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="component-required"
                  checked={editingComponent.is_required}
                  onCheckedChange={(checked) =>
                    setEditingComponent((prev) => (prev ? { ...prev, is_required: checked } : null))
                  }
                />
                <Label htmlFor="component-required">Required Field</Label>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="base-price">Base Price</Label>
                  <Input
                    id="base-price"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={editingComponent.base_price || ""}
                    onChange={(e) =>
                      setEditingComponent((prev) =>
                        prev
                          ? { ...prev, base_price: Number.parseFloat(e.target.value) || 0 }
                          : null
                      )
                    }
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="pricing-type">Pricing Type</Label>
                  <Select
                    value={editingComponent.pricing_type || "fixed"}
                    onValueChange={(value) =>
                      setEditingComponent((prev) =>
                        prev ? { ...prev, pricing_type: value as any } : null
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="per_unit">Per Unit</SelectItem>
                      <SelectItem value="per_sqft">Per Square Foot</SelectItem>
                      <SelectItem value="per_linear">Per Linear Foot</SelectItem>
                      <SelectItem value="formula">Formula</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {editingComponent.pricing_type === "per_unit" && (
                <div>
                  <Label htmlFor="unit-label">Unit Label</Label>
                  <Input
                    id="unit-label"
                    value={editingComponent.unit_label || ""}
                    onChange={(e) =>
                      setEditingComponent((prev) =>
                        prev ? { ...prev, unit_label: e.target.value } : null
                      )
                    }
                    placeholder="e.g., units, pieces, items"
                  />
                </div>
              )}

              {editingComponent.pricing_type === "formula" && (
                <div>
                  <Label htmlFor="formula">Formula</Label>
                  <Input
                    id="formula"
                    value={editingComponent.formula || ""}
                    onChange={(e) =>
                      setEditingComponent((prev) =>
                        prev ? { ...prev, formula: e.target.value } : null
                      )
                    }
                    placeholder="e.g., quantity * base_price * 1.2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: quantity, base_price, price
                  </p>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Switch
                  id="allow-quantity"
                  checked={editingComponent.allow_quantity_input}
                  onCheckedChange={(checked) =>
                    setEditingComponent((prev) =>
                      prev ? { ...prev, allow_quantity_input: checked } : null
                    )
                  }
                />
                <Label htmlFor="allow-quantity">Allow Quantity Input</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="allow-photo"
                  checked={editingComponent.allow_photo_upload}
                  onCheckedChange={(checked) =>
                    setEditingComponent((prev) =>
                      prev ? { ...prev, allow_photo_upload: checked } : null
                    )
                  }
                />
                <Label htmlFor="allow-photo">Allow Photo Upload</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowComponentDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={() => saveComponent(editingComponent)}>Save Component</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateBuilder;
